import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
    PiBellSimpleRingingDuotone,
    PiCalendarDotsBold,
    PiCaretLeftBold,
    PiCaretRightBold,
    PiInfoDuotone,
    PiListBold,
    PiPushPinFill,
    PiSirenDuotone,
    PiWarningDuotone,
} from 'react-icons/pi';
import { Badge, LoadingState, PageHeader } from '@v2/design-system';
import RichListShell, { BadgePill } from '@v2/components/RichListShell';
import type { Column } from '@v2/components/RichListShell';
import api from '@v2/lib/api';
import type { Announcement, CompanyEvent } from '@v2/types/intranet';

// ─── helpers ─────────────────────────────────────────────────────────────────

function annTone(type: Announcement['type']): 'danger' | 'warning' | 'info' | 'primary' {
    switch (type) {
        case 'alert':   return 'danger';
        case 'warning': return 'warning';
        case 'info':    return 'info';
        default:        return 'primary';
    }
}

function annIcon(type: Announcement['type']) {
    switch (type) {
        case 'alert':   return PiSirenDuotone;
        case 'warning': return PiWarningDuotone;
        case 'info':    return PiInfoDuotone;
        default:        return PiBellSimpleRingingDuotone;
    }
}

function evtTone(type: CompanyEvent['type']): 'danger' | 'secondary' | 'primary' | 'warning' | 'success' {
    switch (type) {
        case 'holiday':  return 'danger';
        case 'birthday': return 'secondary';
        case 'meeting':  return 'primary';
        case 'training': return 'warning';
        default:         return 'success';
    }
}

function fmtDate(iso: string) {
    return new Date(iso + 'T00:00:00').toLocaleDateString('pt-BR', {
        day: '2-digit', month: 'short', year: 'numeric',
    });
}

// ─── Announcements page ──────────────────────────────────────────────────────

const ANN_COLS: Column<Announcement>[] = [
    {
        key: 'type',
        label: 'Tipo',
        width: '130px',
        render: (a) => {
            const Icon = annIcon(a.type);
            return (
                <span className="flex items-center gap-1.5">
                    <Icon className="h-4 w-4 shrink-0 text-gray-400" />
                    <BadgePill tone={annTone(a.type)}>{a.type_label}</BadgePill>
                </span>
            );
        },
    },
    {
        key: 'title',
        label: 'Aviso',
        render: (a) => (
            <span className="flex items-center gap-2">
                {a.pinned && <PiPushPinFill className="h-3.5 w-3.5 shrink-0 text-amber-500" title="Fixado" />}
                <span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">{a.title}</span>
                    {a.content && (
                        <span className="mt-0.5 block text-xs text-gray-400 line-clamp-1">
                            {a.content}
                        </span>
                    )}
                </span>
            </span>
        ),
    },
    {
        key: 'created_by',
        label: 'Por',
        width: '140px',
        render: (a) => <span className="text-gray-500">{a.created_by ?? '—'}</span>,
    },
    {
        key: 'created_at',
        label: 'Publicado',
        align: 'right' as const,
        width: '130px',
        render: (a) => (
            <span className="text-gray-500">
                {new Date(a.created_at).toLocaleDateString('pt-BR', {
                    day: '2-digit', month: 'short', year: 'numeric',
                })}
            </span>
        ),
    },
];

export function AnnouncementsPage() {
    const [search, setSearch] = useState('');

    const { data: items = [], isLoading } = useQuery<Announcement[]>({
        queryKey: ['intranet', 'announcements'],
        queryFn: () => api.get('/intranet/announcements').then((r) => r.data.data),
        staleTime: 5 * 60 * 1000,
    });

    const sorted = [...items].sort((a, b) => Number(b.pinned) - Number(a.pinned));
    const filtered = search.trim()
        ? sorted.filter(
              (a) =>
                  a.title.toLowerCase().includes(search.toLowerCase()) ||
                  (a.content ?? '').toLowerCase().includes(search.toLowerCase()),
          )
        : sorted;

    return (
        <RichListShell<Announcement>
            title="Avisos da empresa"
            breadcrumb={[{ name: 'Empresa' }, { name: 'Avisos' }]}
            search={search}
            onSearch={setSearch}
            searchPlaceholder="Buscar aviso…"
            isLoading={isLoading}
            columns={ANN_COLS}
            rows={filtered}
            rowKey={(a) => a.id}
            emptyMessage="Nenhum aviso publicado no momento."
            totalLabel={`${filtered.length} aviso${filtered.length !== 1 ? 's' : ''}`}
        />
    );
}

// ─── Events page — list + calendar ───────────────────────────────────────────

const EVT_COLS: Column<CompanyEvent>[] = [
    {
        key: 'event_date',
        label: 'Data',
        width: '80px',
        render: (e) => {
            const d = new Date(e.event_date + 'T00:00:00');
            return (
                <span className="flex h-10 w-10 shrink-0 flex-col items-center justify-center rounded-lg border border-gray-200 bg-gray-50 text-center dark:border-zinc-700 dark:bg-zinc-800">
                    <span className="text-[10px] uppercase leading-none text-gray-400 dark:text-zinc-500">
                        {d.toLocaleDateString('pt-BR', { month: 'short' }).replace('.', '')}
                    </span>
                    <span className="text-sm font-bold leading-tight text-gray-900 dark:text-gray-100">
                        {d.getDate()}
                    </span>
                </span>
            );
        },
    },
    {
        key: 'title',
        label: 'Evento',
        render: (e) => (
            <span>
                <span className="font-medium text-gray-900 dark:text-gray-100">{e.title}</span>
                {e.description && (
                    <span className="mt-0.5 block text-xs text-gray-400 line-clamp-1">{e.description}</span>
                )}
            </span>
        ),
    },
    {
        key: 'type',
        label: 'Tipo',
        width: '140px',
        render: (e) => (
            <span className="flex items-center gap-2">
                <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: e.color }} />
                <BadgePill tone={evtTone(e.type)}>{e.type_label}</BadgePill>
            </span>
        ),
    },
    {
        key: 'all_day',
        label: 'Dia todo',
        align: 'center' as const,
        width: '90px',
        render: (e) => (
            <Badge color={e.all_day ? 'success' : 'secondary'} variant="flat" rounded="pill" size="sm">
                {e.all_day ? 'Sim' : 'Não'}
            </Badge>
        ),
    },
];

// ─── Calendar sub-component ───────────────────────────────────────────────────

const WEEKDAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const MONTHS = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

function buildCalendarDays(year: number, month: number) {
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const daysInPrev = new Date(year, month, 0).getDate();

    const cells: { day: number; month: 'prev' | 'curr' | 'next' }[] = [];

    for (let i = firstDay - 1; i >= 0; i--) {
        cells.push({ day: daysInPrev - i, month: 'prev' });
    }
    for (let d = 1; d <= daysInMonth; d++) {
        cells.push({ day: d, month: 'curr' });
    }
    const remaining = 42 - cells.length;
    for (let d = 1; d <= remaining; d++) {
        cells.push({ day: d, month: 'next' });
    }

    return cells;
}

function EventCalendar({ events }: { events: CompanyEvent[] }) {
    const today = new Date();
    const [curYear, setCurYear] = useState(today.getFullYear());
    const [curMonth, setCurMonth] = useState(today.getMonth());
    const [selectedDay, setSelectedDay] = useState<number | null>(today.getDate());

    // Build a map: "YYYY-MM-DD" → events[]
    const eventMap = events.reduce<Record<string, CompanyEvent[]>>((acc, e) => {
        const key = e.event_date.slice(0, 10);
        if (!acc[key]) acc[key] = [];
        acc[key].push(e);
        return acc;
    }, {});

    const cells = buildCalendarDays(curYear, curMonth);

    const todayKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    const selectedKey = selectedDay !== null
        ? `${curYear}-${String(curMonth + 1).padStart(2, '0')}-${String(selectedDay).padStart(2, '0')}`
        : null;

    const selectedEvents = selectedKey ? (eventMap[selectedKey] ?? []) : [];

    function prevMonth() {
        if (curMonth === 0) { setCurYear(y => y - 1); setCurMonth(11); }
        else setCurMonth(m => m - 1);
        setSelectedDay(null);
    }

    function nextMonth() {
        if (curMonth === 11) { setCurYear(y => y + 1); setCurMonth(0); }
        else setCurMonth(m => m + 1);
        setSelectedDay(null);
    }

    return (
        <div className="ds-table-container rounded-2xl shadow-sm overflow-hidden">
            <div className="grid lg:grid-cols-[1fr_280px]">
                {/* Calendar grid */}
                <div className="p-5">
                    {/* Header */}
                    <div className="mb-4 flex items-center justify-between">
                        <button
                            onClick={prevMonth}
                            className="rounded-lg border border-gray-200 p-1.5 text-gray-500 hover:bg-gray-50 dark:border-zinc-700 dark:hover:bg-zinc-800"
                        >
                            <PiCaretLeftBold className="h-4 w-4" />
                        </button>
                        <span className="font-semibold text-gray-900 dark:text-gray-100">
                            {MONTHS[curMonth]} {curYear}
                        </span>
                        <button
                            onClick={nextMonth}
                            className="rounded-lg border border-gray-200 p-1.5 text-gray-500 hover:bg-gray-50 dark:border-zinc-700 dark:hover:bg-zinc-800"
                        >
                            <PiCaretRightBold className="h-4 w-4" />
                        </button>
                    </div>

                    {/* Weekdays */}
                    <div className="mb-1 grid grid-cols-7">
                        {WEEKDAYS.map((w) => (
                            <div key={w} className="py-1 text-center text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                                {w}
                            </div>
                        ))}
                    </div>

                    {/* Cells */}
                    <div className="grid grid-cols-7 gap-px">
                        {cells.map((cell, idx) => {
                            const key = cell.month === 'curr'
                                ? `${curYear}-${String(curMonth + 1).padStart(2, '0')}-${String(cell.day).padStart(2, '0')}`
                                : null;
                            const hasEvents = key ? (eventMap[key]?.length ?? 0) > 0 : false;
                            const isToday = key === todayKey;
                            const isSelected = cell.month === 'curr' && cell.day === selectedDay;
                            const isCurr = cell.month === 'curr';

                            return (
                                <button
                                    key={idx}
                                    disabled={!isCurr}
                                    onClick={() => isCurr && setSelectedDay(cell.day)}
                                    className={[
                                        'relative flex flex-col items-center rounded-lg py-2 text-sm transition',
                                        !isCurr ? 'text-gray-300 dark:text-zinc-700 cursor-default' : 'cursor-pointer hover:bg-gray-100 dark:hover:bg-zinc-800',
                                        isSelected ? 'bg-primary text-white hover:bg-primary dark:hover:bg-primary' : '',
                                        isToday && !isSelected ? 'font-bold text-primary dark:text-primary' : '',
                                        isCurr && !isSelected ? 'text-gray-800 dark:text-gray-200' : '',
                                    ].join(' ')}
                                >
                                    {cell.day}
                                    {hasEvents && isCurr && (
                                        <span className={`mt-0.5 h-1.5 w-1.5 rounded-full ${isSelected ? 'bg-white/80' : 'bg-primary'}`} />
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Sidebar: events for selected day */}
                <div className="border-t border-gray-100 p-5 lg:border-l lg:border-t-0 dark:border-zinc-800">
                    <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-400">
                        {selectedKey
                            ? new Date(selectedKey + 'T00:00:00').toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long' })
                            : 'Selecione um dia'}
                    </p>

                    {selectedEvents.length === 0 ? (
                        <p className="text-sm text-gray-400">Sem eventos neste dia.</p>
                    ) : (
                        <div className="space-y-2.5">
                            {selectedEvents.map((e) => (
                                <div key={e.id} className="rounded-lg border border-gray-100 p-3 dark:border-zinc-800">
                                    <div className="flex items-center gap-2">
                                        <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: e.color }} />
                                        <BadgePill tone={evtTone(e.type)}>{e.type_label}</BadgePill>
                                    </div>
                                    <p className="mt-1.5 text-sm font-medium text-gray-900 dark:text-gray-100">{e.title}</p>
                                    {e.description && (
                                        <p className="mt-0.5 text-xs text-gray-400 line-clamp-2">{e.description}</p>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export function EventsPage() {
    const [view, setView] = useState<'list' | 'calendar'>('calendar');
    const [search, setSearch] = useState('');

    const { data: items = [], isLoading } = useQuery<CompanyEvent[]>({
        queryKey: ['intranet', 'events', 'all-upcoming'],
        queryFn: () =>
            api
                .get('/intranet/events', {
                    params: { from: new Date().toISOString().slice(0, 10) },
                })
                .then((r) => r.data.data),
        staleTime: 10 * 60 * 1000,
    });

    const filtered = search.trim()
        ? items.filter(
              (e) =>
                  e.title.toLowerCase().includes(search.toLowerCase()) ||
                  (e.description ?? '').toLowerCase().includes(search.toLowerCase()),
          )
        : items;

    const toggle = (
        <div className="flex gap-1 rounded-lg border border-gray-200 p-1 dark:border-zinc-700">
            <button
                onClick={() => setView('calendar')}
                className={`flex items-center gap-1.5 rounded-md px-3 py-1 text-sm transition ${view === 'calendar' ? 'bg-primary text-white' : 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-zinc-800'}`}
            >
                <PiCalendarDotsBold className="h-4 w-4" /> Calendário
            </button>
            <button
                onClick={() => setView('list')}
                className={`flex items-center gap-1.5 rounded-md px-3 py-1 text-sm transition ${view === 'list' ? 'bg-primary text-white' : 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-zinc-800'}`}
            >
                <PiListBold className="h-4 w-4" /> Lista
            </button>
        </div>
    );

    if (view === 'calendar') {
        return (
            <div className="space-y-6">
                <PageHeader title="Eventos" breadcrumb={[{ name: 'Empresa' }, { name: 'Eventos' }]}>
                    {toggle}
                </PageHeader>
                {isLoading ? <LoadingState /> : <EventCalendar events={items} />}
            </div>
        );
    }

    return (
        <RichListShell<CompanyEvent>
            title="Eventos"
            breadcrumb={[{ name: 'Empresa' }, { name: 'Eventos' }]}
            headerActions={toggle}
            search={search}
            onSearch={setSearch}
            searchPlaceholder="Buscar evento…"
            isLoading={isLoading}
            columns={EVT_COLS}
            rows={filtered}
            rowKey={(e) => e.id}
            emptyMessage="Nenhum evento nos próximos meses."
            totalLabel={`${filtered.length} evento${filtered.length !== 1 ? 's' : ''}`}
        />
    );
}
